import {useEffect, useRef, useState} from 'react';
import {
    Autocomplete,
    Button,
    Collapse,
    Drawer,
    Group,
    Radio,
    ScrollArea,
    Stack,
    Stepper,
    Text,
} from '@mantine/core';
import {Field, FieldArray, Formik, useFormikContext} from 'formik';
import {
    FormikRadioGroup,
    FormikSelect,
    FormikTextarea,
    FormikTextInput,
    PlatformFieldset,
    FormWithButtons,
} from '@flexinet/ui-components';
import {DeploymentAccountGroupsAutocomplete} from '../../deployment-account-groups/inputs/DeploymentAccountGroupsAutocomplete';
import {useDeploymentAccountGroups} from '../../deployment-account-groups/hooks/useDeploymentAccountGroups';
import {DeploymentAccountGroup} from '../../deployment-account-groups/types';
import {useCodeRepositories} from '../../code-repositories/hooks/useCodeRepositories';
import {useAnalyzeRepositoryQuery, useProvisionPipelineMutation} from '../hooks/usePipelinesQuery';
import {DeploymentAccount, Pipeline, PipelineStage, ProvisionPipelineInput, RepositoryAnalysis} from '../types';
import {CodeRepository} from '../../code-repositories/types';
import {AnalysisResultPanel} from '../components/AnalysisResultPanel';
import {PipelineStagesEditor} from '../components/PipelineStagesEditor';
import {DeploymentAccountsEditor} from '../components/DeploymentAccountsEditor';
import {validateStages} from '../utils/stageLadder';
import {deriveDeploymentAccounts} from '../utils/deploymentAccounts';
import {cloneValuesFrom} from '../utils/clonePipeline';

// FNP-444: when the user picks a different deployment account group on the
// provision form, re-derive `deploymentAccounts` to that group's members
// (FNP-1017: ALL of them, dev included — see deriveDeploymentAccounts above).
// Without this listener, the analyzer-suggested array stays put and
// the user's deliberate OU choice is silently dropped at submit. Preserves any
// `deploymentBucketName` overrides keyed by accountId across the swap so a
// user who edited a bucket name doesn't lose it just by changing the group.
const DeploymentAccountGroupSync = ({
                                        groups,
                                    }: { groups: DeploymentAccountGroup[] }) => {
    const {values, setFieldValue} = useFormikContext<ProvisionPipelineInput>();
    const lastGroupIdRef = useRef<string | undefined>(values.deploymentAccountGroupId);

    useEffect(() => {
        const groupId = values.deploymentAccountGroupId;
        if (!groupId || groupId === lastGroupIdRef.current) {
            lastGroupIdRef.current = groupId;
            return;
        }
        const group = groups.find((g) => g.id === groupId);
        if (!group) {
            lastGroupIdRef.current = groupId;
            return;
        }
        const previousBucketByAccountId: Record<string, string | undefined> = {};
        (values.deploymentAccounts ?? []).forEach((acct: DeploymentAccount) => {
            if (acct?.accountId && acct.deploymentBucketName) {
                previousBucketByAccountId[acct.accountId] = acct.deploymentBucketName;
            }
        });
        const next: DeploymentAccount[] = deriveDeploymentAccounts(
            group.accounts,
            previousBucketByAccountId,
            values.repositoryName,
        );
        setFieldValue('deploymentAccounts', next);
        lastGroupIdRef.current = groupId;
    }, [values.deploymentAccountGroupId, groups, setFieldValue, values.deploymentAccounts, values.repositoryName]);

    return null;
};

interface ProvisionPipelineDrawerProps {
    opened: boolean;
    onClose: () => void;
    // FNP-1017: clone/copy from an existing pipeline — the owner's stated
    // alternative to re-picking every option, and the reason no template layer
    // was reinstated. The source pipeline's stages and deployment accounts SEED
    // the same editable form the analyzer seeds; nothing is applied blind, and
    // every field stays editable. Cloning deliberately reuses provisionPipeline
    // (and its ProvisionPipeline RBAC action) rather than adding a mutation —
    // a clone IS a provision whose defaults came from a record instead of the
    // analyzer, so FNP-709's server-side prod gate applies identically.
    sourcePipeline?: Pipeline;
}

const hasS3Stage = (stages?: PipelineStage[]): boolean =>
    Array.isArray(stages) && stages.some((s) => s?.deployProvider === 'S3');

// FNP-693: a stage is a CloudFormation DEPLOY target (region is meaningful) when its
// stageKind is DEPLOY or it has a non-S3 deployProvider. S3 deploys don't use the
// per-stage region (they sync to a bucket), so they're excluded.
const isDeployStage = (stage?: PipelineStage): boolean =>
    !!stage && (stage.stageKind === 'DEPLOY' || (!!stage.deployProvider && stage.deployProvider !== 'S3'));

// FNP-693: per-stage deploy region options. us-west-2 is the default (preselected) and
// equals the pipeline region — the provisioner treats it as same-region (no cross-region
// action). us-east-1 is required for CloudFront ACM certificate stacks.
const DEFAULT_STAGE_REGION = 'us-west-2';

export const ProvisionPipelineDrawer = ({opened, onClose, sourcePipeline}: ProvisionPipelineDrawerProps) => {

    const cloning = !!sourcePipeline;

    // FNP-1017: a clone already knows its stages and accounts, so it opens
    // directly on the editable step. Analysis is a way to GET a suggestion; a
    // clone is a different way to get one. Both land in the same editor.
    const [active, setActive] = useState(cloning ? 2 : 0);
    const [repositoryName, setRepositoryName] = useState(
        sourcePipeline?.codeRepositoryName ?? '');
    const [branchName, setBranchName] = useState(
        sourcePipeline?.branchName ?? 'master');
    const [analysis, setAnalysis] = useState<RepositoryAnalysis | undefined>(undefined);
    const [showMoreDetails, setShowMoreDetails] = useState(false);

    const {items: repositories} = useCodeRepositories();
    const repoData = repositories.map((r: CodeRepository) => r.name);

    const {items: deploymentAccountGroups} = useDeploymentAccountGroups();

    const {refetch, isFetching: analyzing} = useAnalyzeRepositoryQuery(repositoryName, branchName || undefined);
    const provisionMutation = useProvisionPipelineMutation();

    const handleAnalyze = async () => {
        const result = await refetch();
        if (result.data) {
            setAnalysis(result.data);
            setActive(1);
        }
    };

    // FNP-1017: previously ONE handler sat behind a button whose label flipped
    // between 'Confirm' and 'Edit' by confidence — both did setActive(2), so the
    // label promised a choice that did not exist. There is now exactly one action
    // at this step, honestly named: the analyzer's suggestion PRE-FILLS an editable
    // form on step 3, it is never applied take-it-or-leave-it. `acceptSuggestion`
    // seeds form state from the analysis; the admin edits it there.
    const handleAcceptSuggestion = () => {
        setActive(2);
    };

    // FNP-1017: `stages` is a FIRST-CLASS REQUIRED input. Previously the client
    // could submit without it, and provision-pipeline.ts would silently fall back
    // to analyzer-suggested stages server-side (recording empty stack names). That
    // fail-open path is not preserved: the form refuses to submit an empty or
    // unclassified stage list, so what the admin saw is what gets provisioned.
    //
    // NOTE: this does NOT relax or pre-empt the FNP-709 prod-approval guardrail.
    // An admin may still switch approval OFF on a prod-targeting stage here; the
    // UI warns, and pipelines-api validateProdApprovalGate hard-fails it at
    // provisioning. The server stays authoritative — do not add a client-side
    // bypass of that check.
    const validateProvisionInput = (values: ProvisionPipelineInput) => {
        const stageErrors = validateStages(values.stages);
        if (stageErrors.length === 0) {
            return {};
        }
        return {
            stages: stageErrors
                .map((e) => (e.name ? `${e.name}: ${e.message}` : e.message))
                .join(' '),
        };
    };

    const handleProvision = (values: ProvisionPipelineInput) => {
        // Defence in depth: Formik will not call onSubmit while validate returns
        // errors, but the guard is restated here so a future refactor that swaps
        // the validator cannot reopen the fail-open path.
        if (validateStages(values.stages).length > 0) {
            return;
        }
        provisionMutation.mutate(values, {
            onSuccess: () => {
                onClose();
            },
        });
    };

    // FNP-1017: clone seeds from the source RECORD; otherwise from the analyzer.
    // `id`, `pipelineStatus`, `stackName` and `originalStackName` are deliberately
    // NOT copied — they identify the source pipeline's own provisioned resources,
    // and carrying them onto a new pipeline would point the clone's teardown at
    // the original's stacks. `name` is suffixed rather than reused because
    // pipeline names are unique.
    const cloneValues = cloneValuesFrom(sourcePipeline);

    const initialValues: ProvisionPipelineInput = cloneValues ?? {
        repositoryName: analysis?.repositoryName || repositoryName,
        branchName: analysis?.branchName || branchName || undefined,
        name: analysis?.suggestedPipelineName || '',
        pipelineType: analysis?.suggestedPipelineType || undefined,
        archetype: analysis?.detectedArchetype || undefined,
        buildCommand: analysis?.suggestedBuildCommand || undefined,
        // FNP-693: preselect us-west-2 (the default region) on each DEPLOY stage so the
        // Stage Regions selects render a value rather than blank. Non-deploy stages are
        // left untouched. us-west-2 == the pipeline region, so this changes nothing about
        // what the provisioner emits (no cross-region action).
        stages: (analysis?.suggestedStages ?? []).map((stage) =>
            isDeployStage(stage) ? {...stage, region: stage.region ?? DEFAULT_STAGE_REGION} : stage
        ),
        deploymentAccountGroupId: undefined,
        deploymentAccounts: analysis?.suggestedDeploymentAccounts ?? [],
        description: '',
    };

    const provisionForm = (
            <Formik
                initialValues={initialValues}
                enableReinitialize
                validate={validateProvisionInput}
                onSubmit={handleProvision}
            >
                {({handleSubmit, isSubmitting, ...rest}) => (
                    <FormWithButtons
                        handleSubmit={handleSubmit}
                        isSubmitting={isSubmitting || provisionMutation.isPending}
                        handleCancel={onClose}
                        debug={false}
                        {...rest}
                    >
                        <DeploymentAccountGroupSync groups={deploymentAccountGroups}/>
                        <Stack gap="xl">
                            <PlatformFieldset legend="Pipeline Details">
                                <Field
                                    name="name"
                                    label="Name"
                                    description="Pipeline name"
                                    component={FormikTextInput}
                                    withAsterisk
                                    my="xl"
                                />
                                <Field
                                    name="pipelineType"
                                    label="Pipeline Type"
                                    description="Select the pipeline type"
                                    component={FormikSelect}
                                    data={[
                                        {value: 'APPLICATION', label: 'Application'},
                                        {value: 'API', label: 'API'},
                                        {value: 'DATABASE', label: 'Database'},
                                        {value: 'INFRASTRUCTURE', label: 'Infrastructure'},
                                        {value: 'LIBRARY', label: 'Library'},
                                        {value: 'SERVICE', label: 'Service'},
                                    ]}
                                    withAsterisk
                                    my="xl"
                                />
                            </PlatformFieldset>

                            <PlatformFieldset legend="Deployment">
                                <Field
                                    name="deploymentAccountGroupId"
                                    label="Account Group"
                                    description="Select the deployment account group (optional)"
                                    component={DeploymentAccountGroupsAutocomplete}
                                    my="xl"
                                />

                                {hasS3Stage(rest.values.stages) && (
                                    <FieldArray name="deploymentAccounts">
                                        {() => (
                                            <Stack gap="md" my="xl">
                                                <Text fw={600}>Deployment Buckets (S3 stages only)</Text>
                                                {(rest.values.deploymentAccounts ?? []).length === 0 && (
                                                    <Text size="sm" c="dimmed">
                                                        No deployment accounts suggested. Select an account group above or
                                                        re-analyze the repository.
                                                    </Text>
                                                )}
                                                {(rest.values.deploymentAccounts ?? []).map((account, index) => (
                                                    <PlatformFieldset
                                                        key={`${account.accountId}-${index}`}
                                                        legend={account.accountName
                                                            ? `${account.accountName} (${account.accountId})`
                                                            : account.accountId}
                                                    >
                                                        {account.region && (
                                                            <Text size="sm" c="dimmed" my="xs">
                                                                Region: {account.region}
                                                            </Text>
                                                        )}
                                                        <Field
                                                            name={`deploymentAccounts.${index}.deploymentBucketName`}
                                                            label="Deployment Bucket (S3 stages only)"
                                                            description="e.g. flexi-bot-app-infra-951377762314-ui — pre-populated from convention, edit if needed"
                                                            component={FormikTextInput}
                                                            my="xl"
                                                        />
                                                    </PlatformFieldset>
                                                ))}
                                            </Stack>
                                        )}
                                    </FieldArray>
                                )}
                            </PlatformFieldset>

                            {/* FNP-1017: stages are the substance of the pipeline, so
                                the editor is on the page by default — NOT behind "More
                                details". The admin sees every stage that will be built and
                                every option on it before provisioning. */}
                            <PipelineStagesEditor/>

                            <DeploymentAccountsEditor/>

                            <Group justify="flex-start">
                                <Button
                                    variant="subtle"
                                    onClick={() => setShowMoreDetails((v) => !v)}
                                >
                                    {showMoreDetails ? 'Hide additional details' : 'More details'}
                                </Button>
                            </Group>

                            <Collapse in={showMoreDetails}>
                                <Stack gap="xl">
                                    <PlatformFieldset legend="Additional Details">
                                        <Field
                                            name="description"
                                            label="Description"
                                            description="Pipeline description"
                                            component={FormikTextarea}
                                            my="xl"
                                        />
                                        <Field
                                            name="buildCommand"
                                            label="Build Command"
                                            description="Build command (optional)"
                                            component={FormikTextInput}
                                            my="xl"
                                        />
                                    </PlatformFieldset>

                                    <PlatformFieldset legend="Repository">
                                        <Field
                                            name="repositoryName"
                                            label="Code Repository"
                                            description="Repository name"
                                            component={FormikTextInput}
                                            disabled
                                            my="xl"
                                        />
                                        <Field
                                            name="branchName"
                                            label="Branch Name"
                                            description="Branch name"
                                            component={FormikRadioGroup}
                                            data={[
                                                {label: 'master', value: 'master'},
                                                {label: 'main', value: 'main'},
                                            ]}
                                            my="xl"
                                        />
                                    </PlatformFieldset>

                                </Stack>
                            </Collapse>
                        </Stack>
                    </FormWithButtons>
                )}
            </Formik>
    );

    return (
        <Drawer
            transitionProps={{
                transition: 'slide-up',
                duration: 750,
                timingFunction: 'ease',
            }}
            position="right"
            offset={10}
            opened={opened}
            onClose={onClose}
            title={cloning ? `Clone Pipeline${sourcePipeline?.name ? `: ${sourcePipeline.name}` : ''}` : "Provision Pipeline"}
            padding="xl"
            size="xl"
            scrollAreaComponent={ScrollArea.Autosize}
        >
            {/* FNP-1017: a clone skips the analyze/review steps entirely — its
                defaults come from the source record, not the analyzer — and drops
                straight into the SAME editable form. Provisioning from scratch
                keeps the three-step flow. */}
            {cloning ? provisionForm : (
            <Stepper active={active} onStepClick={setActive} allowNextStepsSelect={false}>
                <Stepper.Step label="Select Repository" description="Choose a repo to analyze">
                    <Stack gap="xl" mt="xl">
                        <Autocomplete
                            label="Code Repository"
                            description="Select the repository to analyze"
                            data={repoData}
                            value={repositoryName}
                            onChange={setRepositoryName}
                            placeholder="Select repository"
                            withAsterisk
                        />
                        <Radio.Group
                            label="Branch Name"
                            description="Select the branch to analyze"
                            value={branchName}
                            onChange={(value) => setBranchName(value)}
                        >
                            <Group mt="xs">
                                <Radio value="master" label="master"/>
                                <Radio value="main" label="main"/>
                            </Group>
                        </Radio.Group>
                        <Group justify="flex-end">
                            <Button
                                onClick={handleAnalyze}
                                loading={analyzing}
                                disabled={!repositoryName}
                            >
                                Analyze
                            </Button>
                        </Group>
                    </Stack>
                </Stepper.Step>

                <Stepper.Step label="Review Analysis" description="Review suggestions">
                    {analysis && (
                        <Stack gap="xl" mt="xl">
                            <AnalysisResultPanel analysis={analysis}/>

                            <Group justify="flex-end">
                                <Button variant="default" onClick={() => setActive(0)}>
                                    Back
                                </Button>
                                <Button onClick={handleAcceptSuggestion}>
                                    Continue to edit stages
                                </Button>
                            </Group>
                        </Stack>
                    )}
                </Stepper.Step>

                <Stepper.Step label="Provision" description="Edit & provision">
                    {provisionForm}
                </Stepper.Step>
            </Stepper>
            )}
        </Drawer>
    );
};
